/**
 * 3D Room Visualizer — where a room's lamps go.
 *
 * Kept apart from the render pipeline (which cannot be loaded under test) so
 * the layout can be tested on its own.
 *
 * Three layouts:
 *   grid  — four lamps where the lines of a noughts-and-crosses grid over the
 *           floor cross (a third of the way in from each wall);
 *   row   — a row down the room's longer side, centred where the settings say;
 *   array — the room FILLED, symmetrically: one lamp about every `spacing`
 *           feet each way, as many across as along in proportion to the room,
 *           each centred in its own patch of floor. The same setting fills a
 *           shed with two and a shop with twelve (see `arrayGrid`).
 *
 * Each lamp has a MOUNT — the point on the ceiling its fixture sits on, with
 * the ceiling's normal there, so on a sloped ceiling the fixture lies flat on
 * the slope — and the LAMP itself, the light source: domes, pots and spots
 * hang a foot or so out from the mount along the normal (a point source inches
 * from the panels scorches a hotspot into them); bars hang lower, on chains.
 */

import * as THREE from 'three';
import { RenderSettings } from './renderSettings';

/** A room is never lit by more lamps than this, however dense the array is asked to be. */
export const MAX_ARRAY_LAMPS = 16;
/** Never more than this many lamps in a line either way. */
const MAX_ARRAY_LINE = 8;

export type Normal = [number, number, number];
const STRAIGHT_DOWN: Normal = [0, -1, 0];

export interface LampPlacement {
  /** The light source (room coordinates). */
  lamp: THREE.Vector3;
  /** Where the fixture sits on the ceiling. */
  mount: THREE.Vector3;
  /** The ceiling's unit normal at the mount, pointing into the room. */
  normal: THREE.Vector3;
}

export interface ArrayGrid {
  cols: number;
  rows: number;
  /** Lamp centres along the room's length, and across its width. */
  xs: number[];
  zs: number[];
}

/**
 * How an array of lamps fills a room `length` × `width`: about one every
 * `spacing` feet each way (so the count follows the room's proportions), at
 * least one, never more than MAX_ARRAY_LAMPS in all — the denser direction is
 * thinned first — each centred in its own equal patch of floor.
 */
export function arrayGrid(spacing: number, length: number, width: number): ArrayGrid {
  const s = Math.max(1, spacing);
  const line = (run: number) => Math.max(1, Math.min(MAX_ARRAY_LINE, Math.round(run / s)));
  let cols = line(length);
  let rows = line(width);
  while (cols * rows > MAX_ARRAY_LAMPS) {
    if (cols > 1 && (rows === 1 || cols / length >= rows / width)) cols--;
    else rows--;
  }
  const centres = (n: number, run: number) => Array.from({ length: n }, (_, i) => ((i + 0.5) / n) * run);
  return { cols, rows, xs: centres(cols, length), zs: centres(rows, width) };
}

/** How many lamps the settings put in a room (what the pipeline has to make). */
export function lampCount(s: RenderSettings, length: number, width: number): number {
  if (s.interiorLayout === 'grid') return 4;
  if (s.interiorLayout === 'array') {
    const g = arrayGrid(s.interiorSpacing, length, width);
    return g.cols * g.rows;
  }
  return Math.max(1, Math.round(s.interiorLamps));
}

/** How far out from the ceiling the light source hangs, by fixture (feet). */
function hang(style: RenderSettings['interiorFixtureStyle']): number {
  return style === 'dome' ? 1.2 : 1.0;
}

/** The radius of what sits on the ceiling, by fixture (feet) — a bar hangs level below it and has none. */
const FIXTURE_RADIUS: Record<RenderSettings['interiorFixtureStyle'], number> = { dome: 0.58, pot: 0.34, bar: 0 };

/**
 * Where a room's lamps go (room coordinates), with the mount of each fixture
 * and the ceiling's normal there.
 */
export function lampPlacements(
  s: RenderSettings, length: number, width: number, height: number,
  /** The ceiling's height over a point of the floor (a sloped ceiling); absent = flat at `height`. */
  ceilingAt: (x: number, z: number) => number = () => height,
  /** The ceiling's normal (into the room) over a point of the floor; absent = straight down. */
  normalAt: (x: number, z: number) => Normal = () => STRAIGHT_DOWN,
): LampPlacement[] {
  const radius = FIXTURE_RADIUS[s.interiorFixtureStyle] ?? 0;
  const at = (x: number, z: number): LampPlacement => {
    let ceiling = ceilingAt(x, z);
    const normal = new THREE.Vector3(...normalAt(x, z)).normalize();
    // A fixture lying across a crease of the ceiling (a ridge) would be tilted
    // to one slope and float off the other: it faces straight down instead,
    // set at the lowest point of the ceiling under it, so the peak sits a
    // little way into its base and it still reads as fixed to the ceiling.
    if (radius > 0) {
      const around: [number, number][] = [[x + radius, z], [x - radius, z], [x, z + radius], [x, z - radius]];
      const straddles = around.some(([ax, az]) => {
        const n = new THREE.Vector3(...normalAt(ax, az)).normalize();
        return n.dot(normal) < 0.9999;
      });
      if (straddles) {
        normal.set(0, -1, 0);
        ceiling = Math.min(ceiling, ...around.map(([ax, az]) => ceilingAt(ax, az)));
      }
    }
    const mount = new THREE.Vector3(x, ceiling, z);
    const lamp = s.interiorFixtureStyle === 'bar'
      ? new THREE.Vector3(x, ceiling * s.interiorHeight, z)
      : mount.clone().addScaledVector(normal, hang(s.interiorFixtureStyle));
    lamp.y = Math.max(0.5, lamp.y);
    return { lamp, mount, normal };
  };

  if (s.interiorLayout === 'grid') {
    return [[1, 1], [2, 1], [1, 2], [2, 2]].map(([i, k]) => at((length * i) / 3, (width * k) / 3));
  }
  if (s.interiorLayout === 'array') {
    const g = arrayGrid(s.interiorSpacing, length, width);
    // Row by row across the width, each row along the length.
    return g.zs.flatMap((z) => g.xs.map((x) => at(x, z)));
  }
  const count = Math.max(1, Math.round(s.interiorLamps));
  const alongX = length >= width;
  const long = alongX ? length : width;
  const short = alongX ? width : length;
  const centre = long * (alongX ? s.interiorX : s.interiorZ);
  const across = short * (alongX ? s.interiorZ : s.interiorX);
  return Array.from({ length: count }, (_, i) => {
    const along = Math.max(0.3, Math.min(long - 0.3, centre + ((i + 0.5) / count - 0.5) * long));
    return at(alongX ? along : across, alongX ? across : along);
  });
}

/** The light sources alone (see lampPlacements). */
export function lampSpots(
  s: RenderSettings, length: number, width: number, height: number,
  ceilingAt: (x: number, z: number) => number = () => height,
  normalAt: (x: number, z: number) => Normal = () => STRAIGHT_DOWN,
): THREE.Vector3[] {
  return lampPlacements(s, length, width, height, ceilingAt, normalAt).map((p) => p.lamp);
}
